import React, { useState } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  Divider,
  Stack,
  Checkbox,
  Button,
} from "@mui/material";

import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { colors } from "../../../../utils";
import {
  getLockAccessCodePermission,
  getLockAccessCodes,
  getLockEmailPermission,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  setLockAccessCodes,
  setLockEmailPermission,
} from "../../../../redux/reducers/segmentBuilder";
import LockSettingModalHeader from "../../../LockSettingsModal/components/LockSettingModalHeader";
import PasswordAccordion from "../../../LockSettingsModal/components/PasswordAccordion";
import EmailAccordion from "../../../LockSettingsModal/components/EmailAccordion";
import EmailListing from "../../../LockSettingsModal/components/EmailListing";
import generateStrongPassword from "../../../LockSettingsModal/generatePassword";

const VIEWS = {
  EMAIL_LIST: "emailList",
  INPUT_VIEW: "default",
};

const WheelLockingSettingsModal = ({
  isOpen,
  handleClose,
  onApplySettings,
  handlePrevious,
}) => {
  const lockAccessCodes = useSelector(getLockAccessCodes);
  const lockEmailPermission = useSelector(getLockEmailPermission);
  const lockAccessCodePermission = useSelector(getLockAccessCodePermission);
  const dispatch = useDispatch();
  const [password, setPassword] = useState("");
  const [view, setView] = useState(VIEWS.INPUT_VIEW);
  const [emailInput, setEmailInput] = useState([]);
  const [keys, setKeys] = useState(lockAccessCodes || []);
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(
    lockAccessCodePermission || false
  );
  const [isEmailEnabled, setIsEmailEnabled] = useState(
    lockEmailPermission || false
  );

  const generateKeyPress = () => {
    setPassword(generateStrongPassword());
  };

  const addKeyToList = () => {
    const trimmedPassword = password.trim();
    if (
      !trimmedPassword ||
      keys.some((key) => key.code === trimmedPassword) ||
      keys.length >= 10
    ) {
      toast.error("Invalid or duplicate Password.");
      setPassword("");
      return;
    }
    setKeys([...keys, { code: trimmedPassword, is_active: true }]);
    setPassword("");
  };

  const handleApplySettings = () => {
    // Check if either password (keys) or email permission (emails) is enabled
    if (keys.length === 0 && emailInput.length === 0) {
      toast.error(
        "Please add at least one key or user permission to apply settings."
      );
      return; // Prevent applying settings if neither is enabled
    }

    // Update Redux state with current local state values
    dispatch(setLockEmailPermission(isEmailEnabled));
    dispatch(
      setLockAccessCodes({ access_codes: keys, is_active: isPasswordEnabled })
    );

    // Pass the lock settings and emails back to parent component
    onApplySettings({
      lock_type: {
        access_code_lock: {
          access_codes: keys,
          is_active: isPasswordEnabled,
        },
        permission_lock: {
          is_active: isEmailEnabled,
        },
      },
      emails: emailInput,
    });

    handleClose();
  };

  const onUpdateButtonPress = () => {
    // For wheel creation, we don't have a wheelId yet, so we'll just show success
    if (emailInput.length > 0) {
      toast.success("Emails will be added when the wheel is created!");
      //   setEmailInput([]);
    }
  };

  const toggleIsActive = (index) => {
    setKeys((prevKeys) =>
      prevKeys.map((keyItem, keyIdx) =>
        keyIdx === index
          ? { ...keyItem, is_active: !keyItem.is_active }
          : keyItem
      )
    );
  };

  return (
    <Modal
      open={isOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Fade in={isOpen}>
        <div
          style={{
            backgroundColor: colors.white,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            padding: "32px 48px",
            width: "60%",
            minWidth: "700px",
            maxHeight: "80%",
            borderRadius: "10px",
            position: "relative",
            zIndex: 1300,
            overflowY: "auto",
          }}
        >
          {view === VIEWS.INPUT_VIEW ? (
            <>
              <LockSettingModalHeader
                showBackButton={view === VIEWS.EMAIL_LIST}
                onBackIconClick={() => setView(VIEWS.INPUT_VIEW)}
              />
              <Divider sx={{ mt: 2, mb: 2 }} variant="middle" />
              <div style={{ overflowY: "auto", maxHeight: "60vh" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    marginBottom: 20,
                  }}
                >
                  <Checkbox
                    style={{ marginTop: 10 }}
                    checked={isPasswordEnabled}
                    onChange={(e) => setIsPasswordEnabled(e.target.checked)}
                  />
                  <PasswordAccordion
                    password={password}
                    setPassword={setPassword}
                    generateKeyPress={generateKeyPress}
                    addKeyToList={addKeyToList}
                    keys={keys}
                    setKeys={setKeys}
                    toggleIsActive={toggleIsActive}
                  />
                </div>
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                >
                  <Checkbox
                    style={{ marginTop: 10 }}
                    checked={isEmailEnabled}
                    onChange={(e) => setIsEmailEnabled(e.target.checked)}
                  />
                  <EmailAccordion
                    onChange={(emailsArray) => setEmailInput(emailsArray)}
                    emails={emailInput}
                    onUpdateButtonPress={onUpdateButtonPress}
                    onUsersIconPress={() => setView(VIEWS.EMAIL_LIST)}
                    // isLoading={isLoading}
                  />
                </div>
              </div>
              <Divider sx={{ mt: 2 }} variant="middle" />
              <Stack
                direction="row"
                sx={{ mt: 2 }}
                gap={1}
                justifyContent="flex-end"
              >
                <Button
                  sx={{
                    color: colors.textSecondary,
                    textTransform: "none",
                    fontFamily: "DM Sans",
                  }}
                  onClick={handlePrevious}
                  variant="text"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApplySettings}
                  variant="text"
                  sx={{
                    color: colors.PrimaryPurple,
                    textTransform: "none",
                    fontFamily: "DM Sans",
                    fontWeight: "500",
                    fontSize: 14,
                  }}
                  disabled={keys.length === 0 && emailInput.length === 0}
                >
                  Apply Settings
                </Button>
              </Stack>
            </>
          ) : (
            <EmailListing
              onGoBackPress={() => setView(VIEWS.INPUT_VIEW)}
              wheelId={null} // No wheel ID for creation flow
            />
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default WheelLockingSettingsModal;
